/**
 * Locale Catalog
 *
 * Preset list of ~55 known locales for the autocomplete UI.
 * Includes all Language enum values + iOS-specific codes + base language codes.
 *
 * Users can also enter arbitrary BCP 47 codes via freeSolo input.
 *
 * @module locale-config/constants
 */

import type { ProjectLocale } from '../types/localeConfig.types';

// ============================================================================
// Catalog Entry (extends ProjectLocale with optional grouping)
// ============================================================================

export interface LocaleCatalogEntry extends ProjectLocale {
  /** Group for UI sorting/sectioning */
  group?: 'common' | 'regional' | 'ios' | 'base' | 'legacy';
}

// ============================================================================
// Full Catalog (~55 entries)
// ============================================================================

export const LOCALE_CATALOG: LocaleCatalogEntry[] = [
  // ─── English ────────────────────────────────────────────────────
  { code: 'en-US', label: 'English (US)', group: 'common' },
  { code: 'en-GB', label: 'English (UK)', group: 'common' },
  { code: 'en-AU', label: 'English (Australia)', group: 'regional' },
  { code: 'en-CA', label: 'English (Canada)', group: 'regional' },
  { code: 'en', label: 'English (generic)', group: 'base' },

  // ─── Spanish ────────────────────────────────────────────────────
  { code: 'es-ES', label: 'Spanish (Spain)', group: 'common' },
  { code: 'es-MX', label: 'Spanish (Mexico)', group: 'common' },
  { code: 'es-419', label: 'Spanish (Latin America)', group: 'ios' },
  { code: 'es', label: 'Spanish (generic)', group: 'base' },

  // ─── Portuguese ─────────────────────────────────────────────────
  { code: 'pt-BR', label: 'Portuguese (Brazil)', group: 'common' },
  { code: 'pt-PT', label: 'Portuguese (Portugal)', group: 'common' },
  { code: 'pt', label: 'Portuguese (generic)', group: 'base' },

  // ─── French ─────────────────────────────────────────────────────
  { code: 'fr', label: 'French', group: 'common' },
  { code: 'fr-CA', label: 'French (Canada)', group: 'regional' },

  // ─── Chinese ────────────────────────────────────────────────────
  { code: 'zh-Hans', label: 'Chinese (Simplified)', group: 'common' },
  { code: 'zh-Hant', label: 'Chinese (Traditional)', group: 'common' },

  // ─── German ─────────────────────────────────────────────────────
  { code: 'de', label: 'German', group: 'common' },
  { code: 'de-AT', label: 'German (Austria)', group: 'ios' },
  { code: 'de-CH', label: 'German (Switzerland)', group: 'ios' },

  // ─── Norwegian ──────────────────────────────────────────────────
  { code: 'no', label: 'Norwegian', group: 'common' },
  { code: 'nb', label: 'Norwegian Bokmal', group: 'ios' },
  { code: 'nn', label: 'Norwegian Nynorsk', group: 'ios' },

  // ─── Other Languages (alphabetical) ────────────────────────────
  { code: 'ar', label: 'Arabic', group: 'common' },
  { code: 'ca', label: 'Catalan', group: 'common' },
  { code: 'hr', label: 'Croatian', group: 'common' },
  { code: 'cs', label: 'Czech', group: 'common' },
  { code: 'da', label: 'Danish', group: 'common' },
  { code: 'nl', label: 'Dutch', group: 'common' },
  { code: 'fi', label: 'Finnish', group: 'common' },
  { code: 'el', label: 'Greek', group: 'common' },
  { code: 'he', label: 'Hebrew', group: 'common' },
  { code: 'hi', label: 'Hindi', group: 'common' },
  { code: 'hu', label: 'Hungarian', group: 'common' },
  { code: 'id', label: 'Indonesian', group: 'common' },
  { code: 'it', label: 'Italian', group: 'common' },
  { code: 'ja', label: 'Japanese', group: 'common' },
  { code: 'ko', label: 'Korean', group: 'common' },
  { code: 'ms', label: 'Malay', group: 'common' },
  { code: 'pl', label: 'Polish', group: 'common' },
  { code: 'ro', label: 'Romanian', group: 'common' },
  { code: 'ru', label: 'Russian', group: 'common' },
  { code: 'sk', label: 'Slovak', group: 'common' },
  { code: 'sv', label: 'Swedish', group: 'common' },
  { code: 'th', label: 'Thai', group: 'common' },
  { code: 'tr', label: 'Turkish', group: 'common' },
  { code: 'uk', label: 'Ukrainian', group: 'common' },
  { code: 'vi', label: 'Vietnamese', group: 'common' },

  // ─── Legacy iOS Codes ──────────────────────────────────────────
  { code: 'iw', label: 'Hebrew (legacy iOS)', group: 'legacy' },
  { code: 'in', label: 'Indonesian (legacy iOS)', group: 'legacy' },
];

// ============================================================================
// Helpers
// ============================================================================

/** Map of code → label for quick lookups */
const _catalogMap = new Map(LOCALE_CATALOG.map(e => [e.code, e.label]));

/**
 * Get display label for a locale code.
 * Returns catalog label if known, otherwise the code itself.
 */
export function getLocaleLabel(code: string): string {
  return _catalogMap.get(code) ?? code;
}

/**
 * Check if a locale code exists in the catalog.
 */
export function isKnownLocale(code: string): boolean {
  return _catalogMap.has(code);
}
